import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdminData, getAdminTitle, getFilterDetail } from './AdminLoader';
import { AdminTable } from './AdminTable';
import { EntityAdminMemberTable } from './EntityAdminMemberTable';
import FilterPanel from './FilterPanel';
import { EntityType } from '../../hooks/users';
import { useIsMobile } from '../../hooks/useIsMobile';

interface FilterState {
  locationId: string;
  unitId: string;
  contractId: string;
  locationName?: string;
  unitName?: string;
  contractName?: string;
}

export default function AdminPanel() {
  const [filters, setFilters] = useState<FilterState>({
    locationId: '',
    unitId: '',
    contractId: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [apiError, setApiError] = useState<string | null>(null);

  const filteredFilters = {
    ...filters,
    locationId: filters.locationId || null,
  };

  const { admins, totalPages, isLoading } = useAdminData(filteredFilters, {
    searchQuery,
    page: currentPage,
    limit: 20,
  });

  const adminTitle = getAdminTitle(filteredFilters);
  const filterDetail = getFilterDetail(filters);

  const clearFilters = (specificFilter?: keyof FilterState) => {
    if (specificFilter) {
      setFilters({
        ...filters,
        [specificFilter]: '',
        [`${specificFilter.replace('Id', '')}Name`]: '',
      });
    } else {
      setFilters({
        locationId: '',
        unitId: '',
        contractId: '',
        locationName: '',
        unitName: '',
        contractName: '',
      });
    }
    setCurrentPage(1);
    setApiError(null);
  };

  const getActiveEntityInfo = (): {
    entityId: string;
    entityType: EntityType;
    entityName: string;
  } | null => {
    if (filters.locationId) {
      return {
        entityId: filters.locationId,
        entityType: 'location',
        entityName: filters.locationName || '',
      };
    }
    if (filters.unitId) {
      return {
        entityId: filters.unitId,
        entityType: 'unit',
        entityName: filters.unitName || '',
      };
    }
    if (filters.contractId) {
      return {
        entityId: filters.contractId,
        entityType: 'contract',
        entityName: filters.contractName || '',
      };
    }
    return null;
  };

  const hasActiveFilters = !!(filters.locationId || filters.contractId || filters.unitId);

  const activeEntityInfo = getActiveEntityInfo();
  const showEntityAdminMemberTable = !!activeEntityInfo;
  const isMobile = useIsMobile();

  const handleFilterChange = (newFilters: FilterState) => {
    setCurrentPage(1);
    setApiError(null);
    setFilters(newFilters);
  };

  return (
    <div className={`relative flex gap-4 ${isMobile ? 'flex-col' : ''}`}>
      <div className={isMobile ? 'w-full' : 'w-1/4'}>
        <FilterPanel filters={filters} onChange={handleFilterChange} />
      </div>

      <div className={isMobile ? 'w-full' : 'w-3/4'}>
        <AnimatePresence mode="wait">
          {showEntityAdminMemberTable ? (
            <motion.div
              key="entity-admin-table"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <EntityAdminMemberTable
                entityId={activeEntityInfo.entityId}
                entityType={activeEntityInfo.entityType}
                entityName={activeEntityInfo.entityName}
                onClearEntityFilter={() => clearFilters()}
              />
            </motion.div>
          ) : (
            <motion.div
              key="admin-table"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <AdminTable
                adminTitle={adminTitle}
                filterDetail={filterDetail}
                hasActiveFilters={hasActiveFilters}
                clearFilters={clearFilters}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                setCurrentPage={setCurrentPage}
                isLoading={isLoading}
                admins={admins}
                totalPages={totalPages}
                currentPage={currentPage}
                className="w-full"
                error={apiError}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
